import { useEffect, useRef, useState } from "react";
import { useStore } from "@ludens-reklame/rubics-v3-react/store/hooks";
import { useCart } from "@ludens-reklame/rubics-v3-react/cart/hooks";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { HorizontalSpacer } from "../../internal-components/Layout.js";
import {
  TopInner,
  Logo,
  TopOuter,
  BottomOuter,
  MobileMenu,
  IconMenu,
  PrimaryAction,
  StyledHeader,
  MiniMenuTop,
  MiniMenuTopWrapper,
  Constrainer,
} from "./styles.js";
import { close, hamburger, search } from "../../icons/index.js";
import { createLinkProps } from "../../hooks/useLinkProps.js";
import { Button } from "../../internal-components/Button.js";
import { SearchBar } from "../../internal-components/Input/SearchBar.js";
import { ThemeSettings } from "../../types/theme.js";
import { MobileNavigation } from "./MobileNavigation.js";

export const Header: React.FunctionComponent = () => {
  const store = useStore<ThemeSettings>();
  const cartInterface = useCart();
  const query = store.context.query;
  const [searchExtended, setSearchExtended] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [menuTop, setMenuTop] = useState<number>(0);
  const headerRef = useRef<HTMLElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // The open mobile menu covers the screen below the header (and search bar).
  const measureMenuTop = () => {
    const bottoms = [headerRef.current, searchRef.current].map(
      (el) => el?.getBoundingClientRect().bottom ?? 0
    );
    setMenuTop(Math.max(0, ...bottoms));
  };

  const toggleMenu = () => {
    if (!menuOpen) measureMenuTop();
    setMenuOpen(!menuOpen);
  };

  // Lock page scroll while the mobile menu is open.
  useEffect(() => {
    if (!menuOpen) return;

    measureMenuTop();
    document.body.classList.add("mobile-menu-open");
    window.addEventListener("resize", measureMenuTop);

    return () => {
      document.body.classList.remove("mobile-menu-open");
      window.removeEventListener("resize", measureMenuTop);
    };
  }, [menuOpen, searchExtended]);

  const site = store.site;
  const props = store.themeSettings;
  const menu = props.headerMenu || [];
  const secondaryMenu = props.headerSecondaryMenu || [];
  const primaryAction = props.headerPrimaryAction;

  return (
    <>
      <StyledHeader ref={headerRef}>
        {secondaryMenu.length > 0 ? (
          <MiniMenuTopWrapper>
            <Constrainer>
              <MiniMenuTop>
                <HorizontalSpacer>
                  <ul>
                    {secondaryMenu.map((item, index) => {
                      if (!item.link) return null;

                      return (
                        <li key={index}>
                          <a
                            aria-label={item.link.ariaLabel || undefined}
                            href={item.link.url}
                            target={item.link.external ? "_blank" : undefined}
                            className="h6"
                          >
                            {item.link?.title}{" "}
                            {item.isCartLink ? (
                              <span>({cartInterface.cart.items.length})</span>
                            ) : null}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </HorizontalSpacer>
              </MiniMenuTop>
            </Constrainer>
          </MiniMenuTopWrapper>
        ) : null}
        <TopOuter>
          <HorizontalSpacer>
            <Constrainer>
              <TopInner>
                {site.logo && (
                  <a href="/" aria-label="Gå til forsiden">
                    <Logo
                      src={transformImageUrl(site.logo, { height: 100 })}
                      alt={site.logo.altText}
                    />
                  </a>
                )}
                <IconMenu className="mobile">
                  <button
                    aria-label={searchExtended ? "Lukk søk" : "Åpne søk"}
                    onClick={() => setSearchExtended(!searchExtended)}
                  >
                    {search}
                  </button>
                  <button
                    aria-label={menuOpen ? "Lukk meny" : "Åpne meny"}
                    onClick={toggleMenu}
                  >
                    {menuOpen ? close : hamburger}
                  </button>
                </IconMenu>
                <div className="desktop">
                  <HeaderSearch initialValue={query.q as string} />
                </div>
              </TopInner>
            </Constrainer>
          </HorizontalSpacer>
        </TopOuter>
      </StyledHeader>
      {searchExtended && (
        <BottomOuter className="mobile" ref={searchRef}>
          <HorizontalSpacer>
            <HeaderSearch autoFocus initialValue={query.q as string} />
          </HorizontalSpacer>
        </BottomOuter>
      )}
      {menuOpen && (
        <MobileMenu className="mobile" as="nav" style={{ top: menuTop }}>
          <MobileNavigation menu={menu} />
          {primaryAction && (
            <PrimaryAction>
              <Button
                as="a"
                $variant="secondary"
                className="button--primary"
                {...createLinkProps(primaryAction)}
              >
                {primaryAction.title}
              </Button>
            </PrimaryAction>
          )}
        </MobileMenu>
      )}
    </>
  );
};

interface HeaderSearchProps {
  autoFocus?: boolean;
  initialValue?: string;
}

const HeaderSearch: React.FunctionComponent<HeaderSearchProps> = ({
  autoFocus,
  initialValue,
}) => {
  const { themeSettings } = useStore<ThemeSettings>();
  const [value, setValue] = useState<string>(initialValue || "");

  return (
    <form action="/sokeresultater" method="GET">
      <SearchBar
        name="q"
        value={value}
        onChange={setValue}
        onClear={() => setValue("")}
        autoFocus={autoFocus}
        placeholder={themeSettings.searchPlaceholder || "Søk"}
      />
    </form>
  );
};
